import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Wallet as WalletIcon, TrendingUp, Sparkles, Users2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_COLOR = {
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  processing: "bg-sky-50 text-sky-800 border-sky-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function Wallet() {
  const [w, setW] = useState(null);
  const [summary, setSummary] = useState(null);
  const [openPayout, setOpenPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(10);
  const [payoutMethod, setPayoutMethod] = useState("bank");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/wallet"), api.get("/wallet/savings-summary")]);
    setW(r1.data);
    setSummary(r2.data);
  };

  useEffect(() => {
    load();
  }, []);

  const requestPayout = async () => {
    setLoading(true);
    try {
      await api.post("/wallet/payout", { method: payoutMethod, amount: parseFloat(payoutAmount), destination: "demo@bank" });
      toast.success("Payout request submitted!");
      setOpenPayout(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally {
      setLoading(false);
    }
  };

  if (!w) return <div className="text-gray-500">Loading wallet…</div>;

  return (
    <div data-testid="page-wallet">
      <PageHeader
        eyebrow="Wallet"
        title="Cashback wallet"
        subtitle="Track pending, approved, and paid cashback. Cash out anytime."
        action={
          <Dialog open={openPayout} onOpenChange={setOpenPayout}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="open-payout">
                Request payout
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Request payout</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Amount</label>
                  <Input type="number" min="10" step="1" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} data-testid="payout-amount" />
                  <p className="text-xs text-gray-500 mt-1">Approved balance: {fmtMoney(w.wallet.approved)}. Min: $10.</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Method</label>
                  <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                    <SelectTrigger data-testid="payout-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank transfer</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="giftcard">Gift card</SelectItem>
                      <SelectItem value="usdc">USDC stablecoin (coming soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={requestPayout} disabled={loading} data-testid="confirm-payout" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  {loading ? "Submitting…" : "Submit request"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Approved" value={fmtMoney(w.wallet.approved)} icon={WalletIcon} accent="emerald" testId="w-approved" />
        <StatCard label="Pending" value={fmtMoney(w.wallet.pending)} icon={TrendingUp} accent="sky" testId="w-pending" />
        <StatCard label="Lifetime" value={fmtMoney(w.wallet.lifetime)} icon={Sparkles} accent="amber" testId="w-lifetime" />
        <StatCard label="Referral earnings" value={fmtMoney(w.wallet.referral_earnings)} icon={Users2} accent="emerald" testId="w-referral" />
      </div>

      {summary && (
        <Card className="border-gray-200 shadow-none rounded-2xl mb-6">
          <CardContent className="p-5 grid sm:grid-cols-3 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Annual projection</div>
              <div className="font-display font-bold text-2xl mt-1">{fmtMoney(summary.projected_annual)}</div>
              <div className="text-xs text-gray-500 mt-1">Based on your current monthly average</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Transactions</div>
              <div className="font-display font-bold text-2xl mt-1">{summary.transaction_count}</div>
              <div className="text-xs text-gray-500 mt-1">Confirmed cashback events</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">Top categories</div>
              <div className="text-sm mt-1 space-y-0.5">
                {Object.entries(w.by_category).sort(([,a],[,b]) => b-a).slice(0, 3).map(([cat, val]) => (
                  <div key={cat} className="flex justify-between"><span className="capitalize text-gray-600">{cat}</span><span className="font-semibold">{fmtMoney(val)}</span></div>
                ))}
                {Object.keys(w.by_category).length === 0 && <div className="text-gray-400">No transactions yet</div>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="txns">
        <TabsList className="mb-6">
          <TabsTrigger value="txns" data-testid="tab-txns">Cashback transactions</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>
        <TabsContent value="txns">
          <Card className="border-gray-200 shadow-none rounded-2xl">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr className="text-left">
                    <th className="px-5 py-3 font-semibold text-gray-700">Merchant</th>
                    <th className="px-5 py-3 font-semibold text-gray-700">Order</th>
                    <th className="px-5 py-3 font-semibold text-gray-700">Cashback</th>
                    <th className="px-5 py-3 font-semibold text-gray-700">Status</th>
                    <th className="px-5 py-3 font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {w.transactions.map((t) => (
                    <tr key={t.id} className="border-b border-gray-100" data-testid={`txn-${t.id}`}>
                      <td className="px-5 py-3 flex items-center gap-2">
                        {t.merchant_logo && <img src={t.merchant_logo} alt="" className="w-7 h-7 rounded object-contain bg-gray-50 border border-gray-100 p-0.5" />}
                        <span className="font-medium">{t.merchant_name}</span>
                      </td>
                      <td className="px-5 py-3">{fmtMoney(t.order_amount)}</td>
                      <td className="px-5 py-3 text-emerald-700 font-semibold">+{fmtMoney(t.cashback)}</td>
                      <td className="px-5 py-3"><Badge className={`${STATUS_COLOR[t.status] || ""} border`}>{t.status}</Badge></td>
                      <td className="px-5 py-3 text-gray-500">{new Date(t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {w.transactions.length === 0 && (
                    <tr><td colSpan="5" className="px-5 py-10 text-center text-gray-500">No transactions yet. Shop a deal to earn cashback.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts">
          <Card className="border-gray-200 shadow-none rounded-2xl">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr className="text-left">
                    <th className="px-5 py-3 font-semibold">Method</th>
                    <th className="px-5 py-3 font-semibold">Amount</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {w.payouts.map((p) => (
                    <tr key={p.id} className="border-b border-gray-100">
                      <td className="px-5 py-3 capitalize">{p.method}</td>
                      <td className="px-5 py-3 font-semibold">{fmtMoney(p.amount)}</td>
                      <td className="px-5 py-3"><Badge className={`${STATUS_COLOR[p.status] || ""} border`}>{p.status}</Badge></td>
                      <td className="px-5 py-3 text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {w.payouts.length === 0 && (
                    <tr><td colSpan="4" className="px-5 py-10 text-center text-gray-500">No payouts yet.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
